import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Briefcase, Check } from 'lucide-react';
import { LakshLogo } from '@/components/LakshLogo';
import axios from 'axios';
import { getErrorMessage } from '@/utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CASignupPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    firm_name: '',
    membership_number: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/ca/auth/signup`, formData);
      localStorage.setItem('laksh_ca_token', response.data.token);
      localStorage.setItem('laksh_ca_user', JSON.stringify(response.data.user));
      localStorage.setItem('laksh_ca_firm', JSON.stringify(response.data.firm));
      toast.success('Welcome to the CA Partner Programme!');
      navigate('/ca/dashboard');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    '20% recurring revenue share on all client subscriptions',
    'Unified compliance calendar for all clients',
    'One-click GST filing and ITC reconciliation',
    'Direct access to Laksh founding team',
    'Priority support and feature requests'
  ];

  return (
    <div className="min-h-screen bg-[#06100A] flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#1D9E75] to-[#1D9E75]/70 p-12 items-center justify-center">
        <div className="max-w-lg text-white">
          <h2 className="text-4xl font-bold font-['Outfit'] mb-6">
            Become a CA Partner
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Join 100+ CAs earning passive income while providing better service to their clients.
          </p>
          <ul className="space-y-4">
            {benefits.map((benefit, i) => (
              <li key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
                <span className="text-white/90">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md space-y-8">
          <div>
            <Link data-testid="ca-signup-back-home" to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
            
            <div className="mb-6">
              <LakshLogo height={40} />
            </div>
            
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-[#1D9E75]/20 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-[#1D9E75]" />
              </div>
              <span className="text-[#1D9E75] font-semibold">CA Partner Portal</span>
            </div>
            
            <h1 className="text-3xl font-bold text-white font-['Outfit']">
              Apply as CA Partner
            </h1>
            <p className="text-gray-400 mt-2">
              Start earning while serving your clients better
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="ca-signup-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-gray-300">Full Name</Label>
                <Input data-testid="ca-signup-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="CA Rakesh Kumar"
                  className="bg-[#0a1a12] border-white/20 text-white"
                  required
                />
              </div>
              <div className="col-span-2">
                <Label className="text-gray-300">ICAI Membership Number</Label>
                <Input data-testid="ca-signup-membership-number"
                  name="membership_number"
                  value={formData.membership_number}
                  onChange={handleChange}
                  placeholder="123456"
                  className="bg-[#0a1a12] border-white/20 text-white font-mono"
                  required
                />
              </div>
              <div className="col-span-2">
                <Label className="text-gray-300">Firm Name</Label>
                <Input data-testid="ca-signup-firm-name"
                  name="firm_name"
                  value={formData.firm_name}
                  onChange={handleChange}
                  placeholder="Kumar & Associates"
                  className="bg-[#0a1a12] border-white/20 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-300">Email</Label>
                <Input data-testid="ca-signup-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="ca@firm.com"
                  className="bg-[#0a1a12] border-white/20 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-300">Phone</Label>
                <Input data-testid="ca-signup-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                  className="bg-[#0a1a12] border-white/20 text-white"
                  required
                />
              </div>
              <div className="col-span-2">
                <Label className="text-gray-300">Password</Label>
                <div className="relative">
                  <Input data-testid="ca-signup-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Min 6 characters"
                    className="bg-[#0a1a12] border-white/20 text-white"
                    required
                  />
                  <button
                  data-testid="ca-toggle-password"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            <Button data-testid="ca-signup-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#1D9E75] hover:bg-[#1D9E75]/90 text-white font-semibold"
            >
              {loading ? 'Submitting...' : 'Apply for Partnership'}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              By applying, you agree to our Partner Terms and Revenue Share Agreement.
            </p>
          </form>

          <p className="text-center text-gray-400">
            Already a partner?{' '}
            <Link data-testid="ca-signup-login-link" to="/ca/login" className="text-[#1D9E75] hover:text-[#1D9E75]/80 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
